import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Edit2, Trash2, X, Save, Loader2, AlertCircle, Beaker,
  RefreshCw, Target, Shield, FileText, ChevronRight,
} from 'lucide-react';
import { confirmDialog } from '../ConfirmDialog';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import './CompetitiveStrategyAdmin.css';

/*
 * CompetitiveStrategyAdmin — super-admin editor for the three Tier-1
 * competitive-strategy tables. Lets editors add/update/delete entries
 * without code review + deploy. Backend at /api/admin/competitive-strategy/*.
 *
 * Three tabs:
 *   Strategies — (target, competitor, product) → counter + fit + strategy
 *   Gates      — per-target regex patterns that force fit:'weak'
 *   Portfolios — per-target prose for AI brief LLM context
 *
 * Plus a sandbox tester at the bottom that exercises the lookup against
 * arbitrary input, so editors can preview the effect of pending changes.
 *
 * Cache is invalidated server-side after every write — changes are
 * visible to /renewals, /displacement, /strategy-brief within milliseconds.
 */

const FIT_OPTIONS = ['strong', 'moderate', 'weak', 'unknown'];

const TABS = [
  { id: 'strategies', label: 'Strategies', icon: Target },
  { id: 'gates', label: 'Domain Gates', icon: Shield },
  { id: 'portfolios', label: 'Portfolios', icon: FileText },
];

const log = logger.vendorAnalysis || console;

// ─── Strategies tab ──────────────────────────────────────────────────

function StrategiesTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | row object
  const [filter, setFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-competitive', 'strategies'],
    queryFn: () => apiClient.get('/api/admin/competitive-strategy/strategies'),
  });

  // Optimistic — patch the cached table immediately, roll back on error,
  // reconcile with the server on settle (house pattern, see StepChecklist).
  const strategiesKey = ['admin-competitive', 'strategies'];

  const saveMutation = useMutation({
    mutationFn: (row) => row.id
      ? apiClient.put(`/api/admin/competitive-strategy/strategies/${row.id}`, row)
      : apiClient.post('/api/admin/competitive-strategy/strategies', row),
    onMutate: async (row) => {
      const previous = queryClient.getQueryData(strategiesKey);
      if (previous) {
        queryClient.setQueryData(strategiesKey, {
          ...previous,
          strategies: row.id
            ? (previous.strategies || []).map(r => (r.id === row.id ? { ...r, ...row } : r))
            : [...(previous.strategies || []), { ...row, id: `temp-${Date.now()}` }],
        });
      }
      setEditing(null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(strategiesKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: strategiesKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.del(`/api/admin/competitive-strategy/strategies/${id}`),
    onMutate: async (id) => {
      const previous = queryClient.getQueryData(strategiesKey);
      if (previous) {
        queryClient.setQueryData(strategiesKey, {
          ...previous,
          strategies: (previous.strategies || []).filter(r => r.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(strategiesKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: strategiesKey }),
  });

  const rows = useMemo(() => {
    const list = data?.strategies || [];
    if (!filter) return list;
    const f = filter.toLowerCase();
    return list.filter(r =>
      (r.target_vendor || '').toLowerCase().includes(f)
      || (r.competitor_vendor || '').toLowerCase().includes(f)
      || (r.competitor_product || '').toLowerCase().includes(f)
      || (r.counter_product || '').toLowerCase().includes(f)
    );
  }, [data, filter]);

  if (isLoading) return <div className="csa-loading"><Loader2 size={16} className="csa-spin" /> Loading strategies…</div>;
  if (error) return <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>;

  return (
    <div className="csa-tab">
      <div className="csa-toolbar">
        <input
          type="search"
          className="csa-search"
          placeholder="Filter by vendor or product…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span className="csa-count">{rows.length} of {data?.strategies?.length || 0}</span>
        <button className="csa-btn csa-btn-primary" onClick={() => setEditing('new')}>
          <Plus size={14} /> New strategy
        </button>
      </div>

      {(saveMutation.isError || deleteMutation.isError) && (
        <div className="csa-error">
          <AlertCircle size={14} /> {(saveMutation.error || deleteMutation.error)?.message} — change reverted.
        </div>
      )}

      <table className="csa-table">
        <thead>
          <tr>
            <th>Target</th>
            <th>Competitor</th>
            <th>Product</th>
            <th>Counter</th>
            <th>Fit</th>
            <th style={{ width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.target_vendor}</td>
              <td>{r.competitor_vendor || <em>(catch-all)</em>}</td>
              <td>{r.competitor_product || <em>(default)</em>}</td>
              <td>{r.counter_product || <em>(none)</em>}</td>
              <td><span className={`csa-fit csa-fit-${r.fit}`}>{r.fit}</span></td>
              <td>
                <button className="csa-btn-icon" title="Edit" onClick={() => setEditing(r)}><Edit2 size={13} /></button>
                <button className="csa-btn-icon" title="Delete"
                  onClick={async () => {
                    if (await confirmDialog(`Delete strategy for ${r.target_vendor} → ${r.competitor_vendor || '(any)'}?`)) {
                      deleteMutation.mutate(r.id);
                    }
                  }}>
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <StrategyEditModal
          row={editing === 'new' ? null : editing}
          onSave={(row) => saveMutation.mutate(row)}
          onCancel={() => setEditing(null)}
          isSaving={saveMutation.isPending}
          error={saveMutation.error}
        />
      )}
    </div>
  );
}

function StrategyEditModal({ row, onSave, onCancel, isSaving, error }) {
  const [form, setForm] = useState(row || {
    target_vendor: '',
    competitor_vendor: '',
    competitor_product: '',
    counter_product: '',
    fit: 'moderate',
    strategy: '',
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="csa-modal-overlay" onClick={onCancel}>
      <div className="csa-modal" onClick={(e) => e.stopPropagation()}>
        <header className="csa-modal-head">
          <h3>{row ? 'Edit strategy' : 'New strategy'}</h3>
          <button className="csa-btn-icon" onClick={onCancel}><X size={16} /></button>
        </header>
        <div className="csa-modal-body">
          <label className="csa-field">
            <span>Target vendor *</span>
            <input value={form.target_vendor} onChange={update('target_vendor')} placeholder="Palo Alto Networks" />
          </label>
          <label className="csa-field">
            <span>Competitor vendor <em>(blank = target's catch-all)</em></span>
            <input value={form.competitor_vendor || ''} onChange={update('competitor_vendor')} placeholder="Cisco" />
          </label>
          <label className="csa-field">
            <span>Competitor product <em>(blank = competitor's catch-all)</em></span>
            <input value={form.competitor_product || ''} onChange={update('competitor_product')} placeholder="Cisco Secure Firewall" />
          </label>
          <label className="csa-field">
            <span>Counter-product <em>(blank = no counter, weak fit)</em></span>
            <input value={form.counter_product || ''} onChange={update('counter_product')} placeholder="Palo Alto NGFW (PA-Series)" />
          </label>
          <label className="csa-field">
            <span>Fit *</span>
            <select value={form.fit} onChange={update('fit')}>
              {FIT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="csa-field">
            <span>Strategy *</span>
            <textarea value={form.strategy} onChange={update('strategy')} rows={6} placeholder="Lead with TCO. Direct firewall replacement story…" />
          </label>
          {error && <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>}
        </div>
        <footer className="csa-modal-foot">
          <button className="csa-btn" onClick={onCancel}>Cancel</button>
          <button className="csa-btn csa-btn-primary" onClick={() => onSave(form)} disabled={isSaving}>
            {isSaving ? <Loader2 size={14} className="csa-spin" /> : <Save size={14} />} Save
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Gates tab ────────────────────────────────────────────────────────

function GatesTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-competitive', 'gates'],
    queryFn: () => apiClient.get('/api/admin/competitive-strategy/gates'),
  });

  // Optimistic — same shape as StrategiesTab.
  const gatesKey = ['admin-competitive', 'gates'];

  const saveMutation = useMutation({
    mutationFn: (row) => row.id
      ? apiClient.put(`/api/admin/competitive-strategy/gates/${row.id}`, row)
      : apiClient.post('/api/admin/competitive-strategy/gates', row),
    onMutate: async (row) => {
      const previous = queryClient.getQueryData(gatesKey);
      if (previous) {
        queryClient.setQueryData(gatesKey, {
          ...previous,
          gates: row.id
            ? (previous.gates || []).map(r => (r.id === row.id ? { ...r, ...row } : r))
            : [...(previous.gates || []), { ...row, id: `temp-${Date.now()}` }],
        });
      }
      setEditing(null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(gatesKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: gatesKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiClient.del(`/api/admin/competitive-strategy/gates/${id}`),
    onMutate: async (id) => {
      const previous = queryClient.getQueryData(gatesKey);
      if (previous) {
        queryClient.setQueryData(gatesKey, {
          ...previous,
          gates: (previous.gates || []).filter(r => r.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(gatesKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: gatesKey }),
  });

  if (isLoading) return <div className="csa-loading"><Loader2 size={16} className="csa-spin" /> Loading gates…</div>;
  if (error) return <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>;

  const rows = data?.gates || [];

  return (
    <div className="csa-tab">
      <div className="csa-toolbar">
        <span className="csa-count">{rows.length} gate patterns</span>
        <button className="csa-btn csa-btn-primary" onClick={() => setEditing('new')}>
          <Plus size={14} /> New gate
        </button>
      </div>

      {(saveMutation.isError || deleteMutation.isError) && (
        <div className="csa-error">
          <AlertCircle size={14} /> {(saveMutation.error || deleteMutation.error)?.message} — change reverted.
        </div>
      )}

      <table className="csa-table">
        <thead>
          <tr>
            <th>Target</th>
            <th>Label</th>
            <th>Pattern (regex)</th>
            <th>Strategy snippet</th>
            <th style={{ width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.target_vendor}</td>
              <td>{r.pattern_label}</td>
              <td><code>{r.pattern_regex}</code></td>
              <td className="csa-truncate" title={r.strategy}>{r.strategy.slice(0, 80)}…</td>
              <td>
                <button className="csa-btn-icon" title="Edit" onClick={() => setEditing(r)}><Edit2 size={13} /></button>
                <button className="csa-btn-icon" title="Delete"
                  onClick={async () => {
                    if (await confirmDialog(`Delete gate "${r.pattern_label}" for ${r.target_vendor}?`)) {
                      deleteMutation.mutate(r.id);
                    }
                  }}>
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <GateEditModal
          row={editing === 'new' ? null : editing}
          onSave={(row) => saveMutation.mutate(row)}
          onCancel={() => setEditing(null)}
          isSaving={saveMutation.isPending}
          error={saveMutation.error}
        />
      )}
    </div>
  );
}

function GateEditModal({ row, onSave, onCancel, isSaving, error }) {
  const [form, setForm] = useState(row || {
    target_vendor: '',
    pattern_regex: '',
    pattern_label: '',
    strategy: '',
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Local regex compile-test for instant feedback (server validates too)
  const regexErr = useMemo(() => {
    if (!form.pattern_regex) return null;
    try { new RegExp(form.pattern_regex, 'i'); return null; }
    catch (e) { return e.message; }
  }, [form.pattern_regex]);

  return (
    <div className="csa-modal-overlay" onClick={onCancel}>
      <div className="csa-modal" onClick={(e) => e.stopPropagation()}>
        <header className="csa-modal-head">
          <h3>{row ? 'Edit gate' : 'New gate'}</h3>
          <button className="csa-btn-icon" onClick={onCancel}><X size={16} /></button>
        </header>
        <div className="csa-modal-body">
          <label className="csa-field">
            <span>Target vendor *</span>
            <input value={form.target_vendor} onChange={update('target_vendor')} placeholder="Palo Alto Networks" />
          </label>
          <label className="csa-field">
            <span>Pattern label *</span>
            <input value={form.pattern_label} onChange={update('pattern_label')} placeholder="Network switches" />
          </label>
          <label className="csa-field">
            <span>Pattern regex * <em>(case-insensitive, applied to competitor product + sample description)</em></span>
            <input value={form.pattern_regex} onChange={update('pattern_regex')} placeholder="\\bswitch(es)?\\b" style={{ fontFamily: 'monospace' }} />
            {regexErr && <span className="csa-inline-error">Regex error: {regexErr}</span>}
          </label>
          <label className="csa-field">
            <span>Strategy *</span>
            <textarea value={form.strategy} onChange={update('strategy')} rows={5} placeholder="Switches are not Palo product space — Cisco/Juniper/Aruba territory. Skip from take-out plays." />
          </label>
          {error && <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>}
        </div>
        <footer className="csa-modal-foot">
          <button className="csa-btn" onClick={onCancel}>Cancel</button>
          <button className="csa-btn csa-btn-primary" onClick={() => onSave(form)} disabled={isSaving || !!regexErr}>
            {isSaving ? <Loader2 size={14} className="csa-spin" /> : <Save size={14} />} Save
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Portfolios tab ───────────────────────────────────────────────────

function PortfoliosTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-competitive', 'portfolios'],
    queryFn: () => apiClient.get('/api/admin/competitive-strategy/portfolios'),
  });

  // Optimistic — same shape as StrategiesTab (portfolios are keyed by
  // target_vendor, and the PUT is an upsert).
  const portfoliosKey = ['admin-competitive', 'portfolios'];

  const saveMutation = useMutation({
    mutationFn: (row) =>
      apiClient.put(`/api/admin/competitive-strategy/portfolios/${encodeURIComponent(row.target_vendor)}`, { summary: row.summary }),
    onMutate: async (row) => {
      const previous = queryClient.getQueryData(portfoliosKey);
      if (previous) {
        const list = previous.portfolios || [];
        const exists = list.some(p => p.target_vendor === row.target_vendor);
        queryClient.setQueryData(portfoliosKey, {
          ...previous,
          portfolios: exists
            ? list.map(p => (p.target_vendor === row.target_vendor ? { ...p, summary: row.summary } : p))
            : [...list, { target_vendor: row.target_vendor, summary: row.summary }],
        });
      }
      setEditing(null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(portfoliosKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: portfoliosKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (target_vendor) =>
      apiClient.del(`/api/admin/competitive-strategy/portfolios/${encodeURIComponent(target_vendor)}`),
    onMutate: async (target_vendor) => {
      const previous = queryClient.getQueryData(portfoliosKey);
      if (previous) {
        queryClient.setQueryData(portfoliosKey, {
          ...previous,
          portfolios: (previous.portfolios || []).filter(p => p.target_vendor !== target_vendor),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(portfoliosKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: portfoliosKey }),
  });

  if (isLoading) return <div className="csa-loading"><Loader2 size={16} className="csa-spin" /> Loading portfolios…</div>;
  if (error) return <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>;

  const rows = data?.portfolios || [];

  return (
    <div className="csa-tab">
      <div className="csa-toolbar">
        <span className="csa-count">{rows.length} portfolios</span>
        <button className="csa-btn csa-btn-primary" onClick={() => setEditing('new')}>
          <Plus size={14} /> New portfolio
        </button>
      </div>

      {(saveMutation.isError || deleteMutation.isError) && (
        <div className="csa-error">
          <AlertCircle size={14} /> {(saveMutation.error || deleteMutation.error)?.message} — change reverted.
        </div>
      )}

      {rows.map((r) => (
        <div key={r.target_vendor} className="csa-portfolio-row">
          <div className="csa-portfolio-head">
            <strong>{r.target_vendor}</strong>
            <div>
              <button className="csa-btn-icon" onClick={() => setEditing(r)} title="Edit"><Edit2 size={13} /></button>
              <button className="csa-btn-icon" title="Delete"
                onClick={async () => {
                  if (await confirmDialog(`Delete portfolio for ${r.target_vendor}?`)) {
                    deleteMutation.mutate(r.target_vendor);
                  }
                }}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          <p className="csa-portfolio-text">{r.summary}</p>
        </div>
      ))}

      {editing && (
        <PortfolioEditModal
          row={editing === 'new' ? null : editing}
          onSave={(row) => saveMutation.mutate(row)}
          onCancel={() => setEditing(null)}
          isSaving={saveMutation.isPending}
          error={saveMutation.error}
        />
      )}
    </div>
  );
}

function PortfolioEditModal({ row, onSave, onCancel, isSaving, error }) {
  const [form, setForm] = useState(row || { target_vendor: '', summary: '' });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="csa-modal-overlay" onClick={onCancel}>
      <div className="csa-modal" onClick={(e) => e.stopPropagation()}>
        <header className="csa-modal-head">
          <h3>{row ? `Edit portfolio: ${row.target_vendor}` : 'New portfolio'}</h3>
          <button className="csa-btn-icon" onClick={onCancel}><X size={16} /></button>
        </header>
        <div className="csa-modal-body">
          <label className="csa-field">
            <span>Target vendor * <em>(primary key — only editable on create)</em></span>
            <input value={form.target_vendor} onChange={update('target_vendor')} disabled={!!row} placeholder="Palo Alto Networks" />
          </label>
          <label className="csa-field">
            <span>Portfolio summary *</span>
            <textarea value={form.summary} onChange={update('summary')} rows={10}
              placeholder="Palo Alto Networks is a security platform: Strata (NGFW + Prisma Access SASE)…" />
          </label>
          {error && <div className="csa-error"><AlertCircle size={14} /> {error.message}</div>}
        </div>
        <footer className="csa-modal-foot">
          <button className="csa-btn" onClick={onCancel}>Cancel</button>
          <button className="csa-btn csa-btn-primary" onClick={() => onSave(form)} disabled={isSaving}>
            {isSaving ? <Loader2 size={14} className="csa-spin" /> : <Save size={14} />} Save
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Sandbox panel ────────────────────────────────────────────────────

function SandboxPanel() {
  const [form, setForm] = useState({
    target_vendor: 'Palo Alto Networks',
    competitor_vendor: 'Cisco',
    competitor_product: '',
    sample_description: '',
  });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const testMutation = useMutation({
    mutationFn: (input) => apiClient.post('/api/admin/competitive-strategy/sandbox', input),
  });

  return (
    <section className="csa-sandbox">
      <header className="csa-sandbox-head">
        <h3><Beaker size={16} /> Sandbox tester</h3>
        <p className="csa-help">Exercise the lookup function against arbitrary input to preview the effect of changes you've made above.</p>
      </header>
      <div className="csa-sandbox-grid">
        <label className="csa-field">
          <span>Target vendor *</span>
          <input value={form.target_vendor} onChange={update('target_vendor')} />
        </label>
        <label className="csa-field">
          <span>Competitor vendor</span>
          <input value={form.competitor_vendor} onChange={update('competitor_vendor')} />
        </label>
        <label className="csa-field">
          <span>Competitor product (top_component)</span>
          <input value={form.competitor_product} onChange={update('competitor_product')} placeholder="e.g. Cisco Catalyst 9000 Series" />
        </label>
        <label className="csa-field">
          <span>Sample description (optional)</span>
          <input value={form.sample_description} onChange={update('sample_description')} />
        </label>
      </div>
      <button className="csa-btn csa-btn-primary" onClick={() => testMutation.mutate(form)} disabled={testMutation.isPending || !form.target_vendor}>
        {testMutation.isPending ? <Loader2 size={14} className="csa-spin" /> : <ChevronRight size={14} />} Test
      </button>

      {testMutation.data && (
        <div className="csa-sandbox-result">
          <div><strong>Counter-product:</strong> {testMutation.data.result.counter_product || <em>(none)</em>}</div>
          <div><strong>Fit:</strong> <span className={`csa-fit csa-fit-${testMutation.data.result.fit}`}>{testMutation.data.result.fit}</span></div>
          <div><strong>Strategy:</strong></div>
          <p className="csa-strategy-text">{testMutation.data.result.strategy}</p>
        </div>
      )}
      {testMutation.error && (
        <div className="csa-error"><AlertCircle size={14} /> {testMutation.error.message}</div>
      )}
    </section>
  );
}

// ─── Cache control ────────────────────────────────────────────────────

function CacheControls() {
  const [reloading, setReloading] = useState(false);
  const [info, setInfo] = useState(null);

  const onReload = async () => {
    setReloading(true);
    try {
      const r = await apiClient.post('/api/admin/competitive-strategy/cache/reload', {});
      setInfo(r.cache);
      log.info?.('CompetitiveStrategy:cache-reloaded', r.cache);
    } catch (e) {
      log.error?.('CompetitiveStrategy:cache-reload-failed', { error: e.message });
    } finally {
      setReloading(false);
    }
  };

  return (
    <div className="csa-cache-controls">
      <button className="csa-btn" onClick={onReload} disabled={reloading} title="Force-reload server-side cache (only needed if you suspect a stale read)">
        {reloading ? <Loader2 size={13} className="csa-spin" /> : <RefreshCw size={13} />} Reload cache
      </button>
      {info && (
        <span className="csa-cache-info">
          loaded {new Date(info.loadedAt).toLocaleTimeString()} · {info.strategy_targets?.length || 0} targets
        </span>
      )}
    </div>
  );
}

// ─── Top-level component ──────────────────────────────────────────────

export default function CompetitiveStrategyAdmin() {
  const [activeTab, setActiveTab] = useState('strategies');

  return (
    <div className="csa">
      <header className="csa-header">
        <h2>Competitive Strategy</h2>
        <p className="csa-help">
          Edit per-vendor counter-products, domain gates, and portfolio summaries. Changes invalidate the
          server-side cache automatically — they're visible in <code>/renewals</code>, <code>/displacement</code>,
          and <code>/strategy-brief</code> within milliseconds.
        </p>
        <CacheControls />
      </header>

      <nav className="csa-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`csa-tab-btn ${activeTab === t.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </nav>

      <div className="csa-tab-content">
        {activeTab === 'strategies' && <StrategiesTab />}
        {activeTab === 'gates' && <GatesTab />}
        {activeTab === 'portfolios' && <PortfoliosTab />}
      </div>

      <SandboxPanel />
    </div>
  );
}
